import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import { v4 as uuidv4 } from "uuid";
import labelCircuito from "../../../../labelCircuito";
import isCircuitoConfigurado from "../../../../isCircuitoConfigurado";
import InternoModalConfirmacaoSaida from "../../../../../../../../components/modalSaindoDaPagina";
import styles from "./styles";

// Modal de seleção em massa: árvore serviço -> circuito com busca, seleção independente do
// filtro e criação de linhas de "novo circuito" pendentes, para aplicar um template em vários
// circuitos do cliente numa única chamada (applyManyDiagramaTemplate, Task 3)
export default function ModalAplicarEmMassa({
  aberto = false,
  onFechar = () => {},
  template = null,
  servicos = [],
  bloqueadoPorPendencia = false,
  onAplicar = () => {},
}) {
  const theme = useTheme();
  const modalStyles = styles(theme);

  // Busca por circuito (labelCircuito) ou por título do serviço, sem diferenciar maiúsculas/minúsculas
  const [busca, setBusca] = useState("");
  // Seleção de circuitos já existentes é um estado independente do filtro (Set de ids) —
  // digitar ou limpar a busca nunca altera quem já estava marcado
  const [selecionados, setSelecionados] = useState(() => new Set());
  // Linhas pendentes de "+ Novo circuito" por serviço: { [clientServiceId]: [{ pendingId, clientServiceId, clienteId }] }
  const [novosPorServico, setNovosPorServico] = useState({});
  const [enviando, setEnviando] = useState(false);
  // Passo de confirmação com os números explícitos antes da aplicação de fato (Task 5, Step 1)
  const [confirmAplicar, setConfirmAplicar] = useState(false);

  // Reseta busca, seleção, linhas pendentes e confirmação a cada abertura do modal
  useEffect(() => {
    if (!aberto) return;
    setBusca("");
    setSelecionados(new Set());
    setNovosPorServico({});
    setEnviando(false);
    setConfirmAplicar(false);
  }, [aberto]);

  const termoBusca = busca?.trim?.()?.toLowerCase?.() || "";

  // Monta a árvore serviço -> circuito já filtrada: um circuito é compatível se o próprio
  // rótulo (labelCircuito) bate com a busca OU o título do serviço (grupo) bate
  const gruposFiltrados = useMemo(() => {
    return (servicos || [])
      .map((servico) => {
        // Só circuitos já persistidos (com id) entram na árvore selecionável — um circuito
        // criado alhures na tela mas ainda não salvo (só tempId) não tem id para aplicar o template
        const itensCircuito = (servico?.circuits || [])
          .filter((circuit) => circuit?.id)
          .map((circuit, index) => ({
            circuit,
            rotulo: labelCircuito(circuit, index),
          }));

        const tituloBate = servico?.titulo?.toLowerCase?.()?.includes?.(termoBusca);
        const itensVisiveis = !termoBusca
          ? itensCircuito
          : tituloBate
            ? itensCircuito
            : itensCircuito.filter((item) =>
                item?.rotulo?.toLowerCase?.()?.includes?.(termoBusca),
              );

        const novos = novosPorServico?.[servico?.clientServiceId] || [];

        return { servico, itensVisiveis, novos };
      })
      // Serviço sem nenhum circuito compatível fica oculto sob busca ativa — as linhas de "novo
      // circuito" pendentes mantêm o grupo visível, já que elas não têm nome real para casar
      .filter((grupo) => !termoBusca || grupo?.itensVisiveis?.length > 0 || grupo?.novos?.length > 0);
  }, [servicos, termoBusca, novosPorServico]);

  // Ids visíveis (sob o filtro ativo) de circuitos já existentes de um grupo
  const idsVisiveisDoGrupo = (grupo) =>
    (grupo?.itensVisiveis || []).map((item) => item?.circuit?.id).filter(Boolean);

  // Estado do checkbox de cabeçalho do grupo: reflete e afeta só os circuitos visíveis (Step 5)
  const estadoSelecaoGrupo = (grupo) => {
    const ids = idsVisiveisDoGrupo(grupo);
    if (!ids.length) return { checked: false, indeterminate: false };
    const marcados = ids.filter((id) => selecionados.has(id));
    return {
      checked: marcados.length === ids.length,
      indeterminate: marcados.length > 0 && marcados.length < ids.length,
    };
  };

  // Marca/desmarca em bloco os circuitos visíveis do grupo
  const toggleGrupo = (grupo) => {
    const ids = idsVisiveisDoGrupo(grupo);
    const { checked } = estadoSelecaoGrupo(grupo);
    setSelecionados((prev) => {
      const novo = new Set(prev);
      ids.forEach((id) => (checked ? novo.delete(id) : novo.add(id)));
      return novo;
    });
  };

  // Marca/desmarca um único circuito
  const toggleCircuito = (id) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  // Acrescenta à seleção só os ids atualmente visíveis sob o filtro; nunca remove o que ficou fora
  const selecionarTodosFiltrados = () => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      gruposFiltrados.forEach((grupo) => {
        idsVisiveisDoGrupo(grupo).forEach((id) => novo.add(id));
      });
      return novo;
    });
  };

  // Acrescenta uma linha pendente de "novo circuito" ao serviço, com o clienteId já resolvido
  // pela regra I6 (contexto de 2.3-circuito/index.js) — nunca recalculado aqui. `nome` começa
  // vazio: se o usuário não preencher, executarAplicacao envia null (rótulo genérico "Circuito N")
  const adicionarNovoCircuito = (servico) => {
    setNovosPorServico((prev) => ({
      ...prev,
      [servico?.clientServiceId]: [
        ...(prev?.[servico?.clientServiceId] || []),
        {
          pendingId: uuidv4(),
          clientServiceId: servico?.clientServiceId,
          clienteId: servico?.clienteId,
          nome: "",
        },
      ],
    }));
  };

  // Remove uma linha pendente específica, individualmente
  const removerNovoCircuito = (servico, pendingId) => {
    setNovosPorServico((prev) => ({
      ...prev,
      [servico?.clientServiceId]: (prev?.[servico?.clientServiceId] || []).filter(
        (linha) => linha?.pendingId !== pendingId,
      ),
    }));
  };

  // Atualiza o nome digitado para uma linha pendente específica de "novo circuito"
  const atualizarNomeNovoCircuito = (servico, pendingId, nome) => {
    setNovosPorServico((prev) => ({
      ...prev,
      [servico?.clientServiceId]: (prev?.[servico?.clientServiceId] || []).map((linha) =>
        linha?.pendingId === pendingId ? { ...linha, nome } : linha,
      ),
    }));
  };

  // Total de linhas pendentes de "novo circuito" somado entre todos os serviços
  const totalNovos = useMemo(
    () =>
      Object.values(novosPorServico || {}).reduce(
        (acc, lista) => acc + (lista?.length || 0),
        0,
      ),
    [novosPorServico],
  );

  const totalSelecionado = selecionados.size + totalNovos;

  // Números explícitos para o passo de confirmação (Task 5, Step 1): quantos circuitos já
  // existentes foram marcados (totalExistentes), quantos deles já tinham diagrama e por isso vão
  // ter o diagrama substituído (totalSubstituidos — substituir apaga os anexos dos nós removidos)
  // e quantos circuitos novos serão criados (totalCriados)
  const totalExistentes = selecionados.size;
  const totalCriados = totalNovos;
  const totalSubstituidos = useMemo(() => {
    const circuitosPorId = new Map();
    (servicos || []).forEach((servico) => {
      (servico?.circuits || []).forEach((circuit) => {
        if (circuit?.id) circuitosPorId.set(circuit.id, circuit);
      });
    });
    return Array.from(selecionados).filter((id) => {
      const circuito = circuitosPorId.get(id);
      return (circuito?.diagramaEquipamentos?.nodes?.length || 0) > 0;
    }).length;
  }, [servicos, selecionados]);

  // A seleção continua navegável com pendência (D7) — só a aplicação em si é bloqueada
  const podeAplicar =
    !bloqueadoPorPendencia && !enviando && totalSelecionado > 0 && !!template;

  // Botão "Aplicar template" só abre o passo de confirmação (Step 1); a chamada de fato só
  // acontece depois de confirmado, em executarAplicacao
  const handleAplicar = () => {
    if (!podeAplicar) return;
    setConfirmAplicar(true);
  };

  // Monta o payload e delega a aplicação de fato ao chamador (onAplicar), que já embute a
  // chamada ao service e a reidratação (Task 5, Step 2). Erro é tudo-ou-nada (Step 4): fecha só a
  // confirmação e mantém este modal aberto com a seleção intacta para nova tentativa — o
  // snackbar de erro já é responsabilidade de quem forneceu onAplicar
  const executarAplicacao = async () => {
    setEnviando(true);
    try {
      const novosCircuitos = Object.values(novosPorServico || {})
        .flat()
        .map(({ clientServiceId, clienteId, nome }) => ({
          clientServiceId,
          clienteId,
          // Vazio (usuário não preencheu) vira null — mantém o rótulo genérico "Circuito N"
          nome: nome?.trim?.() ? nome.trim() : null,
        }));
      await onAplicar?.({
        circuitoIds: Array.from(selecionados),
        novosCircuitos,
      });
      setConfirmAplicar(false);
      onFechar?.();
    } catch (error) {
      setConfirmAplicar(false);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
    <Dialog
      open={aberto}
      onClose={onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, height: "82vh" } }}
    >
      <DialogTitle sx={modalStyles?.dialogTitle}>
        <Typography variant="h6" fontWeight="bold">
          {`Aplicar "${template?.nome || ""}" em vários circuitos`}
        </Typography>
        <IconButton onClick={onFechar} size="small" aria-label="Fechar">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={modalStyles?.dialogContent}>
        <Box sx={modalStyles?.topBar}>
          <TextField
            label="Buscar circuito ou serviço"
            size="small"
            autoComplete="off"
            value={busca}
            onChange={(evento) => setBusca(evento?.target?.value || "")}
            sx={modalStyles?.buscaField}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={selecionarTodosFiltrados}
            disabled={!gruposFiltrados?.length}
          >
            Selecionar todos (filtrados)
          </Button>
        </Box>

        {gruposFiltrados?.length ? (
          <Box sx={modalStyles?.listaBox}>
            {gruposFiltrados.map((grupo) => {
              const { checked, indeterminate } = estadoSelecaoGrupo(grupo);
              return (
                <Box key={grupo?.servico?.clientServiceId} sx={modalStyles?.grupoBox}>
                  <Box sx={modalStyles?.grupoHeader}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={() => toggleGrupo(grupo)}
                      disabled={!idsVisiveisDoGrupo(grupo).length}
                    />
                    <Typography sx={modalStyles?.grupoTitulo}>
                      {grupo?.servico?.titulo}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddOutlined fontSize="small" />}
                      onClick={() => adicionarNovoCircuito(grupo?.servico)}
                    >
                      Novo circuito
                    </Button>
                  </Box>

                  {grupo?.itensVisiveis?.map((item) => {
                    const id = item?.circuit?.id;
                    // Badge visível antes de qualquer seleção: o operador precisa saber o que vai substituir
                    const temDiagrama =
                      (item?.circuit?.diagramaEquipamentos?.nodes?.length || 0) > 0;
                    const configurado = isCircuitoConfigurado(item?.circuit);
                    return (
                      <Box key={id} sx={modalStyles?.circuitoRow}>
                        <Checkbox
                          size="small"
                          checked={selecionados.has(id)}
                          onChange={() => toggleCircuito(id)}
                        />
                        <Typography sx={modalStyles?.circuitoLabel}>
                          {item?.rotulo}
                        </Typography>
                        {configurado && (
                          <Box sx={modalStyles?.chipConfigurado}>CONFIGURADO</Box>
                        )}
                        {temDiagrama && (
                          <Box sx={modalStyles?.badgeDiagrama}>Já tem diagrama</Box>
                        )}
                      </Box>
                    );
                  })}

                  {grupo?.novos?.map((linha) => (
                    <Box key={linha?.pendingId} sx={modalStyles?.novoCircuitoRow}>
                      <TextField
                        size="small"
                        variant="standard"
                        autoComplete="off"
                        placeholder="Novo circuito"
                        value={linha?.nome || ""}
                        onChange={(evento) =>
                          atualizarNomeNovoCircuito(
                            grupo?.servico,
                            linha?.pendingId,
                            evento?.target?.value || "",
                          )
                        }
                        sx={modalStyles?.novoCircuitoField}
                      />
                      <IconButton
                        size="small"
                        aria-label="Remover novo circuito"
                        onClick={() => removerNovoCircuito(grupo?.servico, linha?.pendingId)}
                      >
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography sx={modalStyles?.emptyText}>
            Nenhum serviço encontrado.
          </Typography>
        )}
      </DialogContent>

      <Box sx={modalStyles?.footerBox}>
        <Button variant="contained" fullWidth onClick={handleAplicar} disabled={!podeAplicar}>
          {enviando ? <CircularProgress size={22} /> : "Aplicar template"}
        </Button>
        <Typography
          sx={
            bloqueadoPorPendencia
              ? modalStyles?.aplicarHintBloqueado
              : modalStyles?.aplicarHint
          }
        >
          {bloqueadoPorPendencia
            ? "Salve ou cancele as alterações do circuito aberto antes de aplicar em massa."
            : `${totalSelecionado} circuito(s) selecionado(s)`}
        </Typography>
      </Box>
    </Dialog>

    {/* Passo de confirmação com os números explícitos antes da ação (Task 5, Step 1): severity
        "error" quando algum circuito selecionado já tem diagrama (substituir apaga anexos de nó
        dos equipamentos que saírem), "info" quando é só criação/aplicação em circuito vazio */}
    <InternoModalConfirmacaoSaida
      openConfirmacao={confirmAplicar}
      confirmLoad={enviando}
      setOpenConfirmacao={setConfirmAplicar}
      execute={executarAplicacao}
      cancel={() => setConfirmAplicar(false)}
      severity={totalSubstituidos > 0 ? "error" : "info"}
      title="Aplicar template em massa?"
      headerText={`Aplicar "${template?.nome || ""}" em ${totalSelecionado} circuito(s)?`}
      contentText={`${totalExistentes + totalCriados} circuitos receberão o template · ${totalSubstituidos} terão o diagrama substituído · ${totalCriados} serão criados`}
      confirmText={totalSubstituidos > 0 ? "Aplicar e substituir" : "Aplicar"}
    />
    </>
  );
}
